import { changeAppUserStatus, TAppUserDetail } from '$services/api'
import { FC, useCallback } from 'react'
import DangerZone from '$components/DangerZone'
import DeleteDoubleConfirmPopUp from '$components/DeleteDoubleConfirmPopUp'
import useRequest from 'ahooks/lib/useRequest/src/useRequest'
import { toast } from 'sonner'
import { useRouteSummary } from '$contexts/RouteContext/hooks'
import { Button, ButtonProps, Spinner } from '@genie-fintech/ui/components'
import { History, Trash2 } from 'lucide-react'
import { content } from '$layouts/LayoutWithSidebar/styles.css'
import { useModal } from '$components/Modal/hooks/useModal'
import { flexColumn } from '$styles/common.css'
import { footnote, title } from '@genie-fintech/ui/style/typography'
import { themeVars } from '@genie-fintech/ui/style/theme'
import Modal from '$components/Modal'
import { modalContainer } from './styles.css'
import { container } from '../styles.css'
import { container as ReactivateContainer } from './styles.css'
import StatusHistory from '$components/StatusHistory'

const { colors } = themeVars

const buttonVariants: ButtonProps['styleVariants'] = {
  kind: 'neutral',
  type: 'outlined',
  size: 'small'
}

type Props = Pick<TAppUserDetail, 'status' | 'name'> & {
  buttonRef: React.RefObject<HTMLButtonElement>
  onSuccess: VoidFunction
}

const AppUserStatusSetting: FC<Props> = ({
  status,
  name,
  buttonRef,
  onSuccess
}) => {
  const { route } = useRouteSummary()
  const { appId, userId } = route.params

  const isUserActive = status === 'active'

  const modalActions = useModal({
    closeOnClickOutside: false,
    isFocusMode: true
  })

  const { runAsync: updateAppUserStatus, loading: updatingAppUserStatus } =
    useRequest(changeAppUserStatus, {
      manual: true,
      onSuccess: () => {
        toast.success('Success!')
        onSuccess()
      }
    })

  const onUpdateUserStatus = useCallback(
    (status: TAppUserDetail['status']) => {
      if (!appId || !userId) return
      updateAppUserStatus({ appId, userId, status })
    },
    [appId, userId, updateAppUserStatus]
  )

  const onTerminateUser = useCallback(() => {
    onUpdateUserStatus('inactive')
  }, [onUpdateUserStatus])

  const onReactivateUser = useCallback(() => {
    onUpdateUserStatus('active')
  }, [onUpdateUserStatus])

  const ViewHistoryButton = () => (
    <Button styleVariants={buttonVariants} onClick={modalActions.openModal}>
      View History
      <History size={16} />
    </Button>
  )

  return (
    <>
      <article className={container}>
        <article className={content}>
          {isUserActive && (
            <DangerZone type="app_user" rightNav={<ViewHistoryButton />}>
              <DeleteDoubleConfirmPopUp
                ref={buttonRef}
                item={{ type: 'user', name }}
                onExecute={onTerminateUser}
                loading={updatingAppUserStatus}
                disabled={!isUserActive}
              >
                <Button
                  styleVariants={{ kind: 'danger', type: 'text' }}
                  disabled={!isUserActive}
                >
                  {isUserActive ? 'Terminate User' : 'User Terminated'}
                  <Trash2 size={16} />
                </Button>
              </DeleteDoubleConfirmPopUp>
            </DangerZone>
          )}

          {!isUserActive && (
            <article className={ReactivateContainer}>
              <article className={flexColumn} style={{ gap: 4, flex: 1 }}>
                <p className={title.two}>User Reactivation</p>
                <p
                  className={footnote.one}
                  style={{ color: colors.neutral[60] }}
                >
                  You can reactivate this user at any time.
                  <br />
                  Once reactivated, the user will regain access to their account
                  and services.
                </p>
              </article>

              <span style={{ flex: 1 }} />

              <ViewHistoryButton />

              <Button
                buttonRef={buttonRef}
                styleVariants={{ kind: 'primary', size: 'small' }}
                disabled={updatingAppUserStatus}
                onClick={onReactivateUser}
              >
                {updatingAppUserStatus && <Spinner />}
                Reactivate User
              </Button>
            </article>
          )}
        </article>
      </article>

      <Modal {...modalActions} contentProps={{ className: modalContainer }}>
        <article>
          <Button
            styleVariants={{ kind: 'neutral', type: 'outlined', size: 'small' }}
            onClick={modalActions.closeModal}
          >
            Back
          </Button>
        </article>

        <p className={title.two}>USER STATUS HISTORY</p>

        <StatusHistory userId={userId} appId={appId} />
      </Modal>
    </>
  )
}

export default AppUserStatusSetting
